package Network

import DataBase.Interface.BaseAdapterInterface
import DataBase.Interface.DumpHttpResponse
import DataBase.Interface.FixJsonType
import DataBase.NewDBAction
import DataBase.getShopUrl
import Structs.Respone
import Utils.fixJson
import Utils.getJsonWithConfig
import Utils.putIfAbsent
import getHttpClient
import io.ktor.client.*
import io.ktor.client.plugins.*
import io.ktor.client.plugins.logging.*
import io.ktor.client.plugins.websocket.*
import io.ktor.client.request.*
import io.ktor.client.request.forms.*
import io.ktor.client.statement.*
import io.ktor.http.*
import io.ktor.websocket.*
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
// kotlinx-datetime 0.7 moved Clock into the stdlib (kotlin.time). System.currentTimeMillis()
// is JVM-only and unavailable in commonMain, so we use the multiplatform Clock instead.
import kotlin.time.Clock

object httpWorker : BaseAdapterInterface {


    var companyName: String = ""
    var companyId: String = ""
    var local: Boolean = false
    var curToken: String = ""
    var curRefToken: String = ""
    private var httpClient = getHttpClient(local)
    private var lastClientUse: Long = 0L
    private const val CLIENT_MAX_IDLE_MS = 60 * 60 * 1000L   // 1 hour – good balance for overnight sleep scenarios

    /**
     * Used when the primary name cannot be reached before a request is sent (DNS, connect, TLS).
     * One direction only: a backup must never map back to its primary, or failures recurse forever.
     */
    private fun backupHost(host: String): String? {
        val shop = hostOf(getShopUrl())
        val shopBackup = if (shop == "nihulkav.shop") "nihulkavshop.com" else "nihulkav.shop"
        return when (host) {
            "nihulkav.online" -> "nihulkavonline.com"
            shop -> shopBackup
            else -> null
        }
    }
    private const val HOST_FALLBACK_TTL_MS = 10 * 60 * 1000L

    /** primary host -> epoch ms until which requests go to its backup. */
    @kotlin.concurrent.Volatile
    private var hostOverrideUntil: Map<String, Long> = emptyMap()

    // Multiplatform "now" in epoch millis (replaces JVM-only System.currentTimeMillis()).
    private fun nowMs(): Long = Clock.System.now().toEpochMilliseconds()

    private fun hostOf(url: String): String? {
        val afterScheme = url.substringAfter("://", "")
        if (afterScheme.isEmpty()) return null
        return afterScheme.substringBefore("/").substringBefore(":").lowercase()
    }

    private fun replaceHost(url: String, from: String, to: String): String {
        val marker = "://"
        val idx = url.indexOf(marker)
        if (idx < 0) return url
        val start = idx + marker.length
        if (!url.regionMatches(start, from, 0, from.length, ignoreCase = true)) return url
        val after = start + from.length
        val next = url.getOrNull(after)
        if (next != null && next != '/' && next != ':') return url
        return url.substring(0, start) + to + url.substring(after)
    }

    /** Rewrite a URL whose primary host failed within [HOST_FALLBACK_TTL_MS]. */
    private fun preferredUrl(url: String): String {
        val host = hostOf(url) ?: return url
        val until = hostOverrideUntil[host] ?: return url
        if (nowMs() >= until) {
            hostOverrideUntil = hostOverrideUntil - host
            return url
        }
        val alt = backupHost(host) ?: return url
        return replaceHost(url, host, alt)
    }

    /**
     * True only when the engine failed before the request reached the server:
     * DNS lookup, TCP connect, or TLS handshake. Request/socket timeouts, resets,
     * and broken pipes are excluded because a POST may already have been applied.
     */
    private fun Throwable.failedBeforeRequest(): Boolean {
        if (this is kotlin.coroutines.cancellation.CancellationException) return false
        val text = buildString {
            var cur: Throwable? = this@failedBeforeRequest
            var n = 0
            while (cur != null && n < 8) {
                append(cur::class.simpleName ?: "")
                append(' ')
                append(cur.message ?: "")
                append(' ')
                cur = cur.cause
                n++
            }
        }.lowercase()
        return listOf(
            // DNS (OkHttp/JVM, Ktor, Darwin)
            "unknownhostexception",
            "unresolvedaddressexception",
            "unable to resolve host",
            "nodename nor servname",
            "name or service not known",
            "no address associated with hostname",
            "temporary failure in name resolution",
            "eai_again",
            "eai_noname",
            "hostname could not be found",
            "code=-1003",
            // TCP connect
            "connecttimeoutexception",
            "connect timeout has expired",
            "connectexception",
            "connection refused",
            "failed to connect to",
            "network is unreachable",
            "no route to host",
            "could not connect to the server",
            "code=-1004",
            // TLS handshake
            "sslhandshakeexception",
            "sslpeerunverifiedexception",
            "certpathvalidatorexception",
            "hostname verification",
            "code=-1202",
        ).any { text.contains(it) }
    }

    /**
     * Ktor JS reports every browser network failure as `Error("Fail to fetch")`, whether or not
     * the request reached the server. It is a Kotlin [Error], so `catch (e: Exception)` misses it.
     */
    private fun Throwable.isBrowserFetchFailure(): Boolean = this is Error && message == "Fail to fetch"

    /** Keep fatal Errors (OOM, StackOverflow) propagating; only a browser fetch failure may fall back. */
    private fun Throwable.rethrowIfFatal() {
        if (this is Error && !isBrowserFetchFailure()) throw this
    }

    /** Callers saw a thrown Error on web before fallback existed; keep that once fallback is not used. */
    private fun Throwable.rethrowIfError() {
        if (this is Error) throw this
    }

    /** Quick GET to the failed origin: any HTTP answer means the host is reachable. */
    private suspend fun originUnreachable(url: String): Boolean {
        val origin = url.substringBefore("://") + "://" + url.substringAfter("://").substringBefore("/")
        val http = HttpClient() {
            install(HttpTimeout) {
                requestTimeoutMillis = 5000
                connectTimeoutMillis = 5000
                socketTimeoutMillis = 5000
            }
        }
        return try {
            http.get(origin)
            false
        } catch (e: kotlin.coroutines.cancellation.CancellationException) {
            throw e
        } catch (_: Throwable) {
            true
        } finally {
            http.close()
        }
    }

    /**
     * Remember the backup and return the rewritten URL, or null if this call should not move.
     * [idempotent] requests (GET) may move on any browser fetch failure; others only when the
     * primary origin itself does not answer, so a POST the server already applied is not re-sent.
     */
    private suspend fun failoverUrl(url: String, error: Throwable, idempotent: Boolean): String? {
        val host = hostOf(url) ?: return null
        val alt = backupHost(host)
        if (alt == null) {
            // The backup failed too: the device is offline or the backup is broken. Drop the
            // override so the next call (reconnect, user retry) goes back to the primary instead
            // of staying pinned to a failing backup for the rest of the TTL.
            val primaries = hostOverrideUntil.keys.filter { backupHost(it) == host }
            if (primaries.isNotEmpty()) hostOverrideUntil = hostOverrideUntil - primaries.toSet()
            return null
        }
        val eligible = when {
            error.failedBeforeRequest() -> true
            error.isBrowserFetchFailure() -> idempotent || originUnreachable(url)
            else -> false
        }
        if (!eligible) return null
        val next = replaceHost(url, host, alt)
        if (next == url) return null
        hostOverrideUntil = hostOverrideUntil + (host to nowMs() + HOST_FALLBACK_TTL_MS)
        println("httpWorker host fallback $url -> $next (${error::class.simpleName}: ${error.message})")
        return next
    }

    fun setHTTPClient() {
        httpClient = getHttpClient(local)
        lastClientUse = nowMs()
    }

    private fun ensureFreshClient() {
        val now = nowMs()
        if (lastClientUse == 0L || now - lastClientUse > CLIENT_MAX_IDLE_MS) {
            setHTTPClient()
        } else {
            lastClientUse = now
        }
    }

    class CustomHttpLogger() : Logger {
        override fun log(message: String) {
            println(message) // Or whatever logging system you want here
        }
    }
    suspend fun pureGetApiBytes(
        url: String,

        token: String? = null,
        requestTimeOut: Long = 30000,
    ): Respone<ByteArray?, NetworkOutput> {
        val url = preferredUrl(url)
        try {
            val http = HttpClient() {
                install(HttpTimeout) {
                    requestTimeoutMillis = requestTimeOut
                    connectTimeoutMillis = 10000
                    socketTimeoutMillis = requestTimeOut + 5000
                }

                //           install(Logging) {
                //               logger = CustomHttpLogger()
                //              level = LogLevel.ALL
                //          }
            }

            val response = http.get(url) {
                headers {
                    append("Accept", "application/json")

                    token?.let { append(HttpHeaders.Authorization, " Bearer $token") }
                }
            }

            return if (response.status == HttpStatusCode.OK || response.status == HttpStatusCode.Created) {
                Respone(response.readBytes(), NetworkOutput.SUCCESS)
            } else {
                Respone(null, NetworkOutput.ERROR_GENERIC)
            }
        } catch (e: Throwable) {
            e.rethrowIfFatal()
            failoverUrl(url, e, idempotent = true)?.let { return pureGetApiBytes(it, token, requestTimeOut) }
            e.rethrowIfError()
            return Respone(null, NetworkOutput.ERROR_GENERIC)
        }
    }
    suspend fun pureGetApi(
        url: String,
        token: String? = null,
        basic: String? = null,
    ): Respone<String?, NetworkOutput> {
        val url = preferredUrl(url)
        try {
            val http = HttpClient() {
                install(HttpTimeout) {
                    requestTimeoutMillis = 30000
                    connectTimeoutMillis = 10000
                    socketTimeoutMillis = 35000
                }
                //           install(Logging) {
                //               logger = CustomHttpLogger()
                //              level = LogLevel.ALL
                //          }
            }

            val response = http.get(url) {
                headers {
                    append("Accept", "application/json")

                    if (basic != null) {
                        append(HttpHeaders.Authorization, " Basic $basic")
                    } else {
                        token?.let { append(HttpHeaders.Authorization, " Bearer $it") }
                    }
                }
            }

            return if (response.status == HttpStatusCode.OK || response.status == HttpStatusCode.Created) {
                Respone(response.bodyAsText(), NetworkOutput.SUCCESS)
            } else {
                Respone(null, NetworkOutput.ERROR_GENERIC)
            }
        } catch (e: Throwable) {
            e.rethrowIfFatal()
            failoverUrl(url, e, idempotent = true)?.let { return pureGetApi(it, token, basic) }
            e.rethrowIfError()
            return Respone(null, NetworkOutput.ERROR_GENERIC)
        }
    }

    /** GET with arbitrary headers (e.g. Chatwoot `api_access_token`). Does not add Bearer. */
    suspend fun pureGetApiWithHeaders(
        url: String,
        headersMap: Map<String, String>,
        requestTimeOut: Long = 30000,
        connectTimeOut: Long = 10000,
    ): Respone<String?, NetworkOutput> {
        val url = preferredUrl(url)
        try {
            val http = HttpClient() {
                install(HttpTimeout) {
                    requestTimeoutMillis = requestTimeOut
                    connectTimeoutMillis = connectTimeOut
                    socketTimeoutMillis = requestTimeOut + 5000
                }
            }

            val response = http.get(url) {
                headers {
                    append("Accept", "application/json")
                    headersMap.forEach { (k, v) -> append(k, v) }
                }
            }

            return if (response.status == HttpStatusCode.OK || response.status == HttpStatusCode.Created) {
                Respone(response.bodyAsText(), NetworkOutput.SUCCESS)
            } else {
                Respone(null, NetworkOutput.ERROR_GENERIC)
            }
        } catch (e: Throwable) {
            e.rethrowIfFatal()
            failoverUrl(url, e, idempotent = true)?.let {
                return pureGetApiWithHeaders(it, headersMap, requestTimeOut, connectTimeOut)
            }
            e.rethrowIfError()
            return Respone(null, NetworkOutput.ERROR_GENERIC)
        }
    }

    suspend fun getWithEncodedUrl(
        url: String,
        params: MutableMap<String, String>,
        token: String? = null,
        doPost: Boolean = false,
    ): Respone<String?, NetworkOutput> {
        val url = preferredUrl(url)
        try {
            val http = HttpClient() {
                install(HttpTimeout) {
                    requestTimeoutMillis = 30000
                    connectTimeoutMillis = 10000
                    socketTimeoutMillis = 35000
                }
                //           install(Logging) {
                //               logger = CustomHttpLogger()
                //              level = LogLevel.ALL
                //          }
            }

            val response = if(doPost) http.post(url) {
                headers {
                    append("Accept", "application/json")

                    token?.let { append(HttpHeaders.Authorization, " Bearer $token") }
                }
                url {
                    params.forEach { p ->
                        parameters.append(p.key, p.value)
                    }
                }
            }  else http.get(url) {
                headers {
                    append("Accept", "application/json")

                    token?.let { append(HttpHeaders.Authorization, " Bearer $token") }
                }
                url {
                    params.forEach { p ->
                        parameters.append(p.key, p.value)
                    }
                }
            }

            return if (response.status == HttpStatusCode.OK || response.status == HttpStatusCode.Created) {
                Respone(response.bodyAsText(), NetworkOutput.SUCCESS)
            } else {
                Respone(null, NetworkOutput.ERROR_GENERIC)
            }
        } catch (e: Throwable) {
            e.rethrowIfFatal()
            failoverUrl(url, e, idempotent = !doPost)?.let { return getWithEncodedUrl(it, params, token, doPost) }
            e.rethrowIfError()
            return Respone(null, NetworkOutput.ERROR_GENERIC)
        }
    }
    suspend fun purePostApi(
        url: String,
        params: MutableMap<String, String>?,
        token: String? = null,
        headerStr: String? = null,
        jsonStr: String = "{}",
        withContentHeader: String? = "application/json",
        basic: String? = null,
        requestTimeOut: Long = 60000,
        connectTimeOut : Long = 10000,
        headers: Map<String, String>? = null
    ): Respone<String?, NetworkOutput> {
        val url = preferredUrl(url)
        try {
            val http = HttpClient() {
                install(HttpTimeout) {
                    requestTimeoutMillis = requestTimeOut
                    connectTimeoutMillis = connectTimeOut
                    socketTimeoutMillis = requestTimeOut
                }
                //           install(Logging) {
                //               logger = CustomHttpLogger()
                //              level = LogLevel.ALL
                //          }
            }

            val response = http.post(url) {
                headers {
                    append("Accept", "application/json")

                    headerStr?.let { append("Document-Type", " ${it}") }
                    token?.let { append(HttpHeaders.Authorization, " Bearer $token") }
                    basic?.let { append(HttpHeaders.Authorization, " Basic $basic") }
                    headers?.forEach {
                        append(it.key, it.value)
                    }
                }
                if (params != null) {
                    setBody(FormDataContent(Parameters.build {
                        params.forEach { p ->
                            append(p.key, p.value)
                        }
                    }))
                } else {
                    // Content-Type header is engine-managed in Ktor 2+; must go through contentType()
                    if (withContentHeader != null)
                        contentType(ContentType.parse(withContentHeader))
                    setBody(jsonStr)
                }
            }

            return if (response.status == HttpStatusCode.OK || response.status == HttpStatusCode.Created) {
                Respone(response.bodyAsText(), NetworkOutput.SUCCESS)
            } else {
                // Ktor 1.x threw on non-2xx (expectSuccess was true by default), so callers
                // historically received a diagnostic string in .first; keep that contract.
                Respone("${response.status}: ${response.bodyAsText()}", NetworkOutput.ERROR_GENERIC)
            }
        } catch (e: Throwable) {
            e.rethrowIfFatal()
            failoverUrl(url, e, idempotent = false)?.let {
                return purePostApi(it, params, token, headerStr, jsonStr, withContentHeader, basic, requestTimeOut, connectTimeOut, headers)
            }
            e.rethrowIfError()
            return Respone(e.message, NetworkOutput.ERROR_GENERIC)
        }
    }

    /** PATCH a JSON body to an external API with an optional Bearer token.
     *  Mirrors [purePostApi]; used by the AI drafts client to edit a draft. */
    suspend fun purePatchApi(
        url: String,
        token: String? = null,
        jsonStr: String = "{}",
        withContentHeader: String? = "application/json",
        basic: String? = null,
        requestTimeOut: Long = 60000,
        connectTimeOut: Long = 10000,
    ): Respone<String?, NetworkOutput> {
        val url = preferredUrl(url)
        try {
            val http = HttpClient() {
                install(HttpTimeout) {
                    requestTimeoutMillis = requestTimeOut
                    connectTimeoutMillis = connectTimeOut
                    socketTimeoutMillis = requestTimeOut
                }
            }
            val response = http.patch(url) {
                headers {
                    append("Accept", "application/json")
                    if (basic != null) {
                        append(HttpHeaders.Authorization, " Basic $basic")
                    } else {
                        token?.let { append(HttpHeaders.Authorization, " Bearer $token") }
                    }
                }
                if (withContentHeader != null)
                    contentType(ContentType.parse(withContentHeader))
                setBody(jsonStr)
            }
            return if (
                response.status == HttpStatusCode.OK ||
                response.status == HttpStatusCode.Created ||
                response.status == HttpStatusCode.NoContent
            ) {
                Respone(response.bodyAsText(), NetworkOutput.SUCCESS)
            } else {
                Respone("${response.status}: ${response.bodyAsText()}", NetworkOutput.ERROR_GENERIC)
            }
        } catch (e: Throwable) {
            e.rethrowIfFatal()
            failoverUrl(url, e, idempotent = false)?.let {
                return purePatchApi(it, token, jsonStr, withContentHeader, basic, requestTimeOut, connectTimeOut)
            }
            e.rethrowIfError()
            return Respone(e.message, NetworkOutput.ERROR_GENERIC)
        }
    }

    /** DELETE a resource on an external API with an optional Bearer token.
     *  Mirrors [purePatchApi]; used by the AI drafts client to delete a draft. */
    suspend fun pureDeleteApi(
        url: String,
        token: String? = null,
        requestTimeOut: Long = 60000,
        connectTimeOut: Long = 10000,
    ): Respone<String?, NetworkOutput> {
        val url = preferredUrl(url)
        try {
            val http = HttpClient() {
                install(HttpTimeout) {
                    requestTimeoutMillis = requestTimeOut
                    connectTimeoutMillis = connectTimeOut
                    socketTimeoutMillis = requestTimeOut
                }
            }
            val response = http.delete(url) {
                headers {
                    append("Accept", "application/json")
                    token?.let { append(HttpHeaders.Authorization, " Bearer $token") }
                }
            }
            return if (response.status == HttpStatusCode.OK || response.status == HttpStatusCode.NoContent) {
                Respone(response.bodyAsText(), NetworkOutput.SUCCESS)
            } else {
                Respone("${response.status}: ${response.bodyAsText()}", NetworkOutput.ERROR_GENERIC)
            }
        } catch (e: Throwable) {
            e.rethrowIfFatal()
            failoverUrl(url, e, idempotent = false)?.let { return pureDeleteApi(it, token, requestTimeOut, connectTimeOut) }
            e.rethrowIfError()
            return Respone(e.message, NetworkOutput.ERROR_GENERIC)
        }
    }

    /**
     * POST multipart/form-data (e.g. Chatwoot outgoing message with [attachments]).
     * Uses [submitFormWithBinaryData] + [append] with [ByteArray] like [upload_file] — avoids
     * streaming/size mismatches that trigger OkHttp `ProtocolException: unexpected end of stream`.
     */
    suspend fun purePostMultipartApi(
        url: String,
        fields: Map<String, String>,
        fileFieldName: String,
        fileName: String,
        fileBytes: ByteArray,
        fileContentType: String = "application/pdf",
        extraHeaders: Map<String, String>? = null,
        requestTimeOut: Long = 120000,
        connectTimeOut: Long = 10000,
    ): Respone<String?, NetworkOutput> {
        val url = preferredUrl(url)
        val http = HttpClient() {
            install(HttpTimeout) {
                requestTimeoutMillis = requestTimeOut
                connectTimeoutMillis = connectTimeOut
                socketTimeoutMillis = requestTimeOut
            }
        }
        try {
            val response: HttpResponse = http.submitFormWithBinaryData(
                url = url,
                formData = formData {
                    fields.forEach { (k, v) -> append(k, v) }
                    append(fileFieldName, fileBytes, Headers.build {
                        append(HttpHeaders.ContentType, fileContentType)
                        append(
                            HttpHeaders.ContentDisposition,
                            "form-data; name=\"$fileFieldName\"; filename=\"$fileName\"",
                        )
                    })
                },
            ) {
                headers {
                    append("Accept", "application/json")
                    extraHeaders?.forEach { append(it.key, it.value) }
                }
            }
            return if (response.status == HttpStatusCode.OK || response.status == HttpStatusCode.Created) {
                Respone(response.bodyAsText(), NetworkOutput.SUCCESS)
            } else {
                // Same contract as purePostApi: surface the HTTP error in .first (Ktor 1.x
                // used to throw here and callers got e.message).
                Respone("${response.status}: ${response.bodyAsText()}", NetworkOutput.ERROR_GENERIC)
            }
        } catch (e: Throwable) {
            e.rethrowIfFatal()
            failoverUrl(url, e, idempotent = false)?.let {
                return purePostMultipartApi(
                    it, fields, fileFieldName, fileName, fileBytes, fileContentType, extraHeaders, requestTimeOut, connectTimeOut,
                )
            }
            e.rethrowIfError()
            return Respone(e.message, NetworkOutput.ERROR_GENERIC)
        } finally {
            http.close()
        }
    }

    override suspend fun purePost(
        url: String,
        params: MutableMap<String, String>,
        token: String?,
        urlEnc: Boolean
    ): Respone<String?, NetworkOutput> {
        val url = preferredUrl(url)
        ensureFreshClient()
        try {
            val response = httpClient.post(url) {
                headers {
                    append("Accept", "application/json")
                    token?.let { append(HttpHeaders.Authorization, " Bearer $token") }
                }

                if (urlEnc) {
                    setBody(FormDataContent(
                        (Parameters.build {
                            params.forEach { p ->
                                append(p.key, p.value)
                            }

                        }
                                )))
                } else {
                    setBody(MultiPartFormDataContent(
                        formData {
                            params.forEach { p ->
                                append(p.key, p.value)
                            }

                        }
                    ))
                }
            }
            if (response.status == HttpStatusCode.OK) {
                lastClientUse = nowMs()
                return Respone(response.bodyAsText(), NetworkOutput.SUCCESS)
            } else {
                println("purePost -> bad status, replacing HttpClient and retrying once")
                setHTTPClient()
                // One-shot retry with fresh client (purePost has no retry counter)
                val retryResponse = httpClient.post(url) {
                    headers {
                        append("Accept", "application/json")
                        token?.let { append(HttpHeaders.Authorization, " Bearer $token") }
                    }

                    if (urlEnc) {
                        setBody(FormDataContent(
                            (Parameters.build {
                                params.forEach { p ->
                                    append(p.key, p.value)
                                }

                            }
                                    )))
                    } else {
                        setBody(MultiPartFormDataContent(
                            formData {
                                params.forEach { p ->
                                    append(p.key, p.value)
                                }

                            }
                        ))
                    }
                }
                return if (retryResponse.status == HttpStatusCode.OK) {
                    lastClientUse = nowMs()
                    Respone(retryResponse.bodyAsText(), NetworkOutput.SUCCESS)
                } else {
                    Respone(null, NetworkOutput.ERROR_GENERIC)
                }
            }
        } catch (e: Throwable) {
            e.rethrowIfFatal()
            failoverUrl(url, e, idempotent = false)?.let { return purePost(it, params, token, urlEnc) }
            e.rethrowIfError()
            println(e.stackTraceToString())
            return Respone(null, NetworkOutput.ERROR_GENERIC)
        }
    }


    private fun analyzeToken(response: JsonObject) {
        if ("token" in response) {
            val tokstr = response["token"]!!.toString()
            val tok = simpleFixJson(tokstr.substring(1, tokstr.length - 1))
            val tokObj = getJsonWithConfig().parseToJsonElement(tok) as JsonObject
            curToken = tokObj["access_token"].toString().drop(1).dropLast(1)
            if ("refresh_token" in tokObj) {
                curRefToken = tokObj["refresh_token"].toString().drop(1).dropLast(1)
            }
        }

    }

    private fun analyzeResponse(response: JsonObject): NetworkOutput {
        if (response["status"].toString().subSequence(1, response["status"].toString().length - 1) == "ERROR") {
            var msg = response["status_msg"].toString()
            when {
                msg.contains("id is not found") -> return NetworkOutput.ERROR_ID_NOT_EXIST
                msg.contains("Internal Error") -> return NetworkOutput.ERROR_GENERIC
                msg.contains("not found in args") -> return NetworkOutput.ERROR_ARG_MISSING
                msg.contains("Duplicate") -> return NetworkOutput.ERROR_DUPLICATE
                msg.contains("Empty Query") -> return NetworkOutput.ERROR_EMPTY_QUERY
                msg.contains("CONNECTION_ERROR") -> return NetworkOutput.CONNECTION_ERROR
                msg.contains("Token ERROR") -> return NetworkOutput.TOKEN_ERROR
                msg.contains("Token EXPIRED") -> return NetworkOutput.TOKEN_ERROR
                msg.contains("Token FAIL") -> return NetworkOutput.TOKEN_FAIL
                msg.contains("VERSION_CONFLICT") -> return NetworkOutput.VERSION_CONFLICT
            }
            return NetworkOutput.ERROR_GENERIC
        }
        return NetworkOutput.SUCCESS
    }

    fun fixJsonStr(s: String): String {
        var regexp = ""
// replace it with "- and -"
// note that $1 is now ""example"" and $2 is example (without double quote)

        val regex = Regex("(\'[^':{}]+\')")
        return regex.replace(s) { m ->

            "\"${m.value.substring(1, m.value.length - 1).replace("\"", "\'")}\""
        }
    }

    fun fixJsonStr2(s: String): String {
        var regexp = ""
// replace it with "- and -"
// note that $1 is now ""example"" and $2 is example (without double quote)

        val regex = Regex("(\"[^\\[\\]:{}]+\")")
        return regex.replace(s) { m ->

            m.value.replace("\\", "")
        }
    }

    fun fixInternalJsonStr(s: String): String {
        var regexp = ""
// replace it with "- and -"
// note that $1 is now ""example"" and $2 is example (without double quote)

        val regex = Regex("(\"\\[[^\\[]+\\]\")")
        return regex.replace(s) { m ->
            "\"${m.value.substring(1, m.value.length - 1).replace("\\", "\\\\")}\""
        }
    }

    //    fun fixJsonStr(s: String): String {
//        var regexp = ""
//// replace it with "- and -"
//// note that $1 is now ""example"" and $2 is example (without double quote)
//
//        val regex = Regex("(\'[^':{}]*\')")
//        return regex.replace(s) { m ->
//
//            "\"${m.value.substring(1, m.value.length - 1).replace("\"","\'")}\""
//        }
//    }
    fun badResult(): Respone<String?, NetworkOutput> {
        return Respone(null, NetworkOutput.CONNECTION_ERROR)
    }

    fun ligthFixJson(s: String): String {
        return s.replace("\'", "\"")
    }

    fun lightForInnerFixJson(s: String): String {
        return s.replace("\\\'", "#^").replace("\'", "\"").replace("#^", "\\\'")

    }

    fun simpleFixJson(s: String): String {
        var s = fixJson(s)
        val regex = Regex("(\'[^':{}]+\')")
        s = s.replace("\\\"", "#^")
        s = regex.replace(s) { m ->

            "\"${m.value.substring(1, m.value.length - 1).replace("\"", "\'")}\""
        }.replace("\\", "")
        s = s.replace("#^", "\\\"")
        return s
    }

    fun ultimateFixJson(s: String): String {
        var s =
            s
        var regex = Regex("(\'\\[\\{[^\\[\\]]+\\}\\]\')")
        s = regex.replace(s) { m ->

            m.value.replace("\"", "#^").replace("\'", "#$").replace("\\", "#&")
        }
        regex = Regex("(\'\\[\\{[^\\]]+\\[[^\\[]+\\][^\\[\\]]+\\}\\]\')")
        s = regex.replace(s) { m ->

            m.value.replace("\"", "#^").replace("\'", "#$").replace("\\", "#&")
        }
        s = fixJson(s)
        regex = Regex("(\'[^':{}]+\')")
        s = s.replace("\\\"", "#^")
        s = regex.replace(s) { m ->

            "\"${m.value.substring(1, m.value.length - 1).replace("\"", "\'")}\""
        }.replace("\\", "")
        s = (s.replace("#&", "\\").replace("#$", "\"").replace("#^", "\'"))

        regex = Regex("(\"\\[\\{[^\\[\\]]+\\}\\]\")")
        s = regex.replace(s) { m ->
            "\"${fixJson(m.value).substring(1, m.value.length - 1)}\""
        }
        regex = Regex("(\"\\[\\{[^\\]]+\\[[^\\[]+\\][^\\[\\]]+\\}\\]\")")
        s = regex.replace(s) { m ->
            "\"${fixJson(m.value).substring(1, m.value.length - 1)}\""
        }
        s = s.replace("#^", "\\\"")
        return s
    }

    suspend fun refreshToken(): Boolean {
        val url = preferredUrl(NewDBAction.REFRESH_TOKEN.getURL())
        ensureFreshClient()
        try {

            val response = httpClient.post(url) {
                headers {
                    append("Accept", "application/json")
                    append(HttpHeaders.Authorization, " Bearer $curRefToken")
                }
                setBody(MultiPartFormDataContent(
                    formData {
                    }
                ))
            }
            return             if (response.status == HttpStatusCode.OK) {
                lastClientUse = nowMs()
                val text = response.bodyAsText()
                val elem = getJsonWithConfig().parseToJsonElement(text) as JsonObject
                val networkOutput = analyzeResponse(elem)
                analyzeToken(elem)
                networkOutput == NetworkOutput.SUCCESS
            } else {
                // Lazy recovery for refresh token call
                println("refreshToken -> bad status, replacing HttpClient")
                setHTTPClient()
                false
            }
        } catch (e: Throwable) {
            e.rethrowIfFatal()
            if (failoverUrl(url, e, idempotent = false) != null) return refreshToken()
            e.rethrowIfError()
            return false
        }
    }

    suspend fun login(
        url: String,
        params: MutableMap<String, String>,
        autoCompany: Boolean,
        fixJsonType: FixJsonType = FixJsonType.simple,
        recovered: Boolean = false,
    ): Respone<String?, NetworkOutput> {
        val target = preferredUrl(url)
        ensureFreshClient()
        try {
            if (autoCompany)
                params.putIfAbsent("company_id", companyId)


            val response = httpClient.post(target) {
                timeout {
                    requestTimeoutMillis = 20000
                    connectTimeoutMillis = 8000
                    socketTimeoutMillis = 20000
                }
                headers {
                    append("Accept", "application/json")

                }
                setBody(MultiPartFormDataContent(
                    formData {
                        params.forEach { p ->
                            append(p.key, p.value)
                        }

                    }
                ))
            }
            if (response.status == HttpStatusCode.OK) {
                lastClientUse = nowMs()
                val text = response.bodyAsText()
                val elem = getJsonWithConfig().parseToJsonElement(text) as JsonObject
                val networkOutput = analyzeResponse(elem)
                analyzeToken(elem)
                if (networkOutput == NetworkOutput.SUCCESS) {
                    var NKResponse = elem["status_msg"].toString()
                    NKResponse = NKResponse.substring(1, NKResponse.length - 1)
                    NKResponse = when (fixJsonType) {
                        FixJsonType.complex ->
                            ultimateFixJson(NKResponse)

                        FixJsonType.simple -> simpleFixJson(NKResponse)
                        FixJsonType.light -> ligthFixJson(NKResponse)
                        FixJsonType.lightForInner -> lightForInnerFixJson(NKResponse)
                        FixJsonType.NONE -> NKResponse
                    }


                    return Respone(NKResponse, NetworkOutput.SUCCESS)
                } else {
                    return Respone(null, networkOutput)
                }
            } else {
                println("login -> bad status, replacing HttpClient")
                setHTTPClient()
                if (!recovered) return login(url, params, autoCompany, fixJsonType, recovered = true)
                return badResult()
            }
        } catch (e: Throwable) {
            e.rethrowIfFatal()
            failoverUrl(target, e, idempotent = false)?.let {
                return login(it, params, autoCompany, fixJsonType, recovered = true)
            }
            e.rethrowIfError()
            // A client that failed while the radio was down stays "fresh" for an hour
            // (ensureFreshClient bumps lastClientUse on every call). Replace it and retry
            // once on a dead pooled connection; DNS/connect failures mean really offline.
            println("login -> connection failed, replacing HttpClient")
            setHTTPClient()
            if (!recovered && !e.failedBeforeRequest()) {
                return login(url, params, autoCompany, fixJsonType, recovered = true)
            }
            return badResult()
        }
    }

    override suspend fun post(
        url: String,
        params: MutableMap<String, String>,
        autoCompany: Boolean,
        fixJsonType: FixJsonType,
        token_refresh_counter: Int,
        timeout: Long,
        withToken: Boolean,
        dump: Boolean,
        retText: Boolean
    ): Respone<String?, NetworkOutput> {
        val url = preferredUrl(url)
        if (token_refresh_counter == 0) {
            return Respone(null, NetworkOutput.TOKEN_ERROR)
        }
        ensureFreshClient()
        try {
            if (autoCompany)
                params.putIfAbsent("company_id", companyId)
            if(dump)
                params.putIfAbsent("dump", "1")
            val response = httpClient.post(url) {
                timeout {
                    requestTimeoutMillis = timeout
                    connectTimeoutMillis = 10000
                    socketTimeoutMillis = timeout
                }
                headers {
                    append("Accept", "application/json")
                    if(withToken)
                    append(HttpHeaders.Authorization, " Bearer $curToken")
                }
                setBody(MultiPartFormDataContent(
                    formData {
                        params.forEach { p ->
                            append(p.key, p.value)
                        }

                    }
                ))
            }
            if (response.status == HttpStatusCode.OK) {
                lastClientUse = nowMs()
                val text = response.bodyAsText()
                val elem = getJsonWithConfig().parseToJsonElement(text) as JsonObject
                val networkOutput = analyzeResponse(elem)
                when (networkOutput) {
                    NetworkOutput.SUCCESS -> {
                        if (retText) {
                            return Respone(text, NetworkOutput.SUCCESS)
                        }
                        if(dump){
                            return Respone(elem["status_msg"].toString(), NetworkOutput.SUCCESS)
                        }
                        var NKResponse = elem["status_msg"].toString()

                        NKResponse = NKResponse.substring(1, NKResponse.length - 1)
                        NKResponse = when (fixJsonType) {
                            FixJsonType.complex ->
                                ultimateFixJson(NKResponse)

                            FixJsonType.simple -> simpleFixJson(NKResponse)
                            FixJsonType.light -> ligthFixJson(NKResponse)
                            FixJsonType.lightForInner -> lightForInnerFixJson(NKResponse)
                            FixJsonType.NONE -> NKResponse
                        }


                        return Respone(NKResponse, NetworkOutput.SUCCESS)
                    }

                    NetworkOutput.TOKEN_ERROR -> {
                        refreshToken()
                        println("yotam3")

                        return post(
                            url,
                            params,
                            autoCompany,
                            fixJsonType,
                            token_refresh_counter - 1,
                            timeout,
                            withToken,
                            dump,
                            retText,
                        )
                    }

                    else -> {
                        if (retText) {
                            return Respone(text, networkOutput)
                        }
                        // Keep SUCCESS payloads unchanged. On ERROR, pass status_msg so
                        // callers like hashIsraelSign can show the server Hebrew text.
                        // Older clients ignore this field and still key off NetworkOutput.
                        val errMsg = try {
                            val sm = elem["status_msg"]
                            when (sm) {
                                is JsonPrimitive -> sm.content.trim().ifBlank { null }
                                else -> null
                            }
                        } catch (_: Exception) {
                            null
                        }
                        return Respone(errMsg, networkOutput)
                    }
                }
            } else {
                // Non-2xx status — try to recover from token problems even when the
                // server did not return a JSON body with TOKEN_ERROR (e.g. 401/403).
                val body = try { response.bodyAsText() } catch (_: Exception) { "" }
                val looksLikeTokenError = body.contains("Token", ignoreCase = true) ||
                        response.status == HttpStatusCode.Unauthorized ||
                        response.status == HttpStatusCode.Forbidden

                if (looksLikeTokenError && token_refresh_counter > 1) {
                    println("yotam2 -> attempting refresh on non-OK status")
                    refreshToken()
                    return post(
                        url,
                        params,
                        autoCompany,
                        fixJsonType,
                        token_refresh_counter - 1,
                        timeout,
                        withToken,
                        dump,
                        retText,
                    )
                }

                // Lazy recovery: even if it wasn't obviously a token error,
                // the connection may be dead after a long sleep. Replace the
                // HttpClient once and retry (one-shot).
                if (token_refresh_counter > 1) {
                    println("yotam2 -> bad status, replacing HttpClient and retrying once")
                    setHTTPClient()
                    return post(
                        url,
                        params,
                        autoCompany,
                        fixJsonType,
                        token_refresh_counter - 1,
                        timeout,
                        withToken,
                        dump,
                        retText,
                    )
                }

                println("yotam2")
                return badResult()
            }
        } catch (e: Throwable) {
            e.rethrowIfFatal()
            println("yotam")
            println(e.stackTraceToString())
            failoverUrl(url, e, idempotent = false)?.let {
                return post(it, params, autoCompany, fixJsonType, token_refresh_counter, timeout, withToken, dump, retText)
            }
            e.rethrowIfError()
            if (e.toString().contains("Token has expired")) {
                refreshToken()
                return post(
                    url,
                    params,
                    autoCompany,
                    fixJsonType,
                    token_refresh_counter - 1,
                    timeout,
                    withToken,
                    dump,
                    retText,
                )
            }
            println("post -> connection failed, replacing HttpClient")
            setHTTPClient()
            return badResult()
        }
    }

    override suspend fun postDumpWithTotal(
        url: String,
        params: MutableMap<String, String>,
        autoCompany: Boolean,
        token_refresh_counter: Int,
        timeout: Long,
        withToken: Boolean,
    ): Respone<DumpHttpResponse?, NetworkOutput> {
        val url = preferredUrl(url)
        if (token_refresh_counter == 0) {
            return Respone(null, NetworkOutput.TOKEN_ERROR)
        }
        ensureFreshClient()
        try {
            if (autoCompany)
                params.putIfAbsent("company_id", companyId)
            params.putIfAbsent("dump", "1")
            val response = httpClient.post(url) {
                timeout {
                    requestTimeoutMillis = timeout
                    connectTimeoutMillis = 10000
                    socketTimeoutMillis = timeout
                }
                headers {
                    append("Accept", "application/json")
                    if (withToken)
                        append(HttpHeaders.Authorization, " Bearer $curToken")
                }
                setBody(MultiPartFormDataContent(
                    formData {
                        params.forEach { p ->
                            append(p.key, p.value)
                        }
                    }
                ))
            }
            if (response.status == HttpStatusCode.OK) {
                lastClientUse = nowMs()
                val text = response.bodyAsText()
                val elem = getJsonWithConfig().parseToJsonElement(text) as JsonObject
                val networkOutput = analyzeResponse(elem)
                when (networkOutput) {
                    NetworkOutput.SUCCESS -> {
                        val total = (elem["result_total"] as? JsonPrimitive)?.content?.toIntOrNull()
                        val raw = elem["status_msg"].toString()
                        return Respone(DumpHttpResponse(raw, total), NetworkOutput.SUCCESS)
                    }
                    NetworkOutput.TOKEN_ERROR -> {
                        refreshToken()
                        return postDumpWithTotal(
                            url,
                            params,
                            autoCompany,
                            token_refresh_counter - 1,
                            timeout,
                            withToken,
                        )
                    }
                    else -> return Respone(null, networkOutput)
                }
            } else {
                if (token_refresh_counter > 1) {
                    println("postDumpWithTotal -> bad status, replacing HttpClient and retrying")
                    setHTTPClient()
                    return postDumpWithTotal(
                        url,
                        params,
                        autoCompany,
                        token_refresh_counter - 1,
                        timeout,
                        withToken,
                    )
                }
                return Respone(null, NetworkOutput.ERROR_GENERIC)
            }
        } catch (e: Throwable) {
            e.rethrowIfFatal()
            failoverUrl(url, e, idempotent = false)?.let {
                return postDumpWithTotal(it, params, autoCompany, token_refresh_counter, timeout, withToken)
            }
            e.rethrowIfError()
            if (e.toString().contains("Token has expired")) {
                refreshToken()
                return postDumpWithTotal(
                    url,
                    params,
                    autoCompany,
                    token_refresh_counter - 1,
                    timeout,
                    withToken,
                )
            }
            return Respone(null, NetworkOutput.ERROR_GENERIC)
        }
    }

    override suspend fun post_file(
        url: String,
        params: MutableMap<String, String>,
        autoCompany: Boolean,
        token_refresh_counter: Int,
        timeout: Long
    ): Respone<ByteArray?, NetworkOutput> {
        val url = preferredUrl(url)
        ensureFreshClient()
        try {
            if (autoCompany)
                params.putIfAbsent("company", companyName)
            val response = httpClient.post(url) {
                timeout {
                    requestTimeoutMillis = timeout
                    connectTimeoutMillis = 10000
                    socketTimeoutMillis = timeout
                }
                headers {
                    append("Accept", "application/json")
                    append(HttpHeaders.Authorization, " Bearer $curToken")
                }
                setBody(MultiPartFormDataContent(
                    formData {
                        params.forEach { p ->
                            append(p.key, p.value)
                        }

                    }
                ))
            }
            if (response.status == HttpStatusCode.OK) {
                lastClientUse = nowMs()
                return Respone(response.readBytes(), NetworkOutput.SUCCESS)
            } else {
                if (token_refresh_counter > 1) {
                    println("post_file -> bad status, replacing HttpClient and retrying")
                    setHTTPClient()
                    return post_file(url, params, autoCompany, token_refresh_counter - 1, timeout)
                }
                return Respone(null, NetworkOutput.ERROR_GENERIC)
            }
        } catch (e: Throwable) {
            e.rethrowIfFatal()
            failoverUrl(url, e, idempotent = false)?.let { return post_file(it, params, autoCompany, token_refresh_counter, timeout) }
            e.rethrowIfError()
            return Respone(null, NetworkOutput.ERROR_GENERIC)
        }
    }

    override suspend fun upload_img(
        url: String,
        params: MutableMap<String, String>,
        f: ByteArray,
        image_name: String,
        autoCompany: Boolean,
        token_refresh_counter: Int
    ): Respone<String?, NetworkOutput> {
        val url = preferredUrl(url)
        ensureFreshClient()
        try {
            if (autoCompany)
                params.putIfAbsent("company_id", companyId)
            val response: HttpResponse = httpClient.submitFormWithBinaryData(
                url = url,
                formData = formData {
                    params.forEach { p ->
                        append(p.key, p.value)
                    }
                    append("description", image_name)
                    append("image", f, Headers.build {
                        append(HttpHeaders.ContentType, "image/png")
                        append(HttpHeaders.ContentDisposition, "filename=$image_name.png")
                    })
                }
            ) {
                timeout {
                    requestTimeoutMillis = 40000
                    connectTimeoutMillis = 10000
                    socketTimeoutMillis = 40000
                }
                headers {
                    append(HttpHeaders.Authorization, " Bearer $curToken")
                }
            }

            if (response.status == HttpStatusCode.OK) {
                lastClientUse = nowMs()
                val text = response.bodyAsText()
                val elem = getJsonWithConfig().parseToJsonElement(text) as JsonObject
                val networkOutput = analyzeResponse(elem)
                analyzeToken(elem)
                if (networkOutput == NetworkOutput.SUCCESS) {
                    var NKResponse = elem["status_msg"].toString()
                    NKResponse = NKResponse.substring(1, NKResponse.length - 1)
                    NKResponse = simpleFixJson(NKResponse)
                    return Respone(NKResponse, NetworkOutput.SUCCESS)
                } else {
                    return Respone(null, networkOutput)
                }

            } else {
                if (token_refresh_counter > 1) {
                    println("upload_img -> bad status, replacing HttpClient and retrying")
                    setHTTPClient()
                    return upload_img(url, params, f, image_name, autoCompany, token_refresh_counter - 1)
                }
                return Respone(null, NetworkOutput.ERROR_GENERIC)
            }
        } catch (e: Throwable) {
            e.rethrowIfFatal()
            failoverUrl(url, e, idempotent = false)?.let {
                return upload_img(it, params, f, image_name, autoCompany, token_refresh_counter)
            }
            e.rethrowIfError()
            return Respone(null, NetworkOutput.ERROR_GENERIC)
        }
    }

    override suspend fun upload_file(
        url: String,
        params: MutableMap<String, String>,
        f: ByteArray?,
        fixJsonType: FixJsonType,
        autoCompany: Boolean,
        token_refresh_counter: Int,
        f_name: String?,
        f_mime_type: String?
    ): Respone<String?, NetworkOutput> {
        val url = preferredUrl(url)
        ensureFreshClient()
        try {
            if (autoCompany)
                params.putIfAbsent("company_id", companyId)
            val response: HttpResponse = httpClient.submitFormWithBinaryData(
                url = url,
                formData = formData {
                    params.forEach { p ->
                        append(p.key, p.value)
                    }
                    f?.let {
                        append("file", f, Headers.build {
                            append(HttpHeaders.ContentType, f_mime_type ?: "application/pdf")
                            append(HttpHeaders.ContentDisposition, "filename=${f_name ?: "test.pdf"}")
                        })
                    }
                }
            ) {
                timeout {
                    requestTimeoutMillis = 45000
                    connectTimeoutMillis = 10000
                    socketTimeoutMillis = 45000
                }
                headers {
                    append(HttpHeaders.Authorization, " Bearer $curToken")
                }
            }

            if (response.status == HttpStatusCode.OK) {
                lastClientUse = nowMs()
                val text = response.bodyAsText()
                val elem = getJsonWithConfig().parseToJsonElement(text) as JsonObject
                val networkOutput = analyzeResponse(elem)
                analyzeToken(elem)
                if (networkOutput == NetworkOutput.SUCCESS) {
                    var NKResponse = elem["status_msg"].toString()
                    NKResponse = NKResponse.substring(1, NKResponse.length - 1)
                    NKResponse = when (fixJsonType) {
                        FixJsonType.complex ->
                            ultimateFixJson(NKResponse)

                        FixJsonType.simple -> simpleFixJson(NKResponse)
                        FixJsonType.light -> ligthFixJson(NKResponse)
                        FixJsonType.lightForInner -> lightForInnerFixJson(NKResponse)
                        FixJsonType.NONE -> NKResponse
                    }
                    return Respone(NKResponse, NetworkOutput.SUCCESS)
                } else {
                    return Respone(null, networkOutput)
                }

            } else {
                if (token_refresh_counter > 1) {
                    println("upload_file -> bad status, replacing HttpClient and retrying")
                    setHTTPClient()
                    return upload_file(url, params, f, fixJsonType, autoCompany, token_refresh_counter - 1, f_name, f_mime_type)
                }
                return Respone(null, NetworkOutput.ERROR_GENERIC)
            }
        } catch (e: Throwable) {
            e.rethrowIfFatal()
            failoverUrl(url, e, idempotent = false)?.let {
                return upload_file(it, params, f, fixJsonType, autoCompany, token_refresh_counter, f_name, f_mime_type)
            }
            e.rethrowIfError()
            return Respone(null, NetworkOutput.ERROR_GENERIC)
        }
    }

    suspend fun pureGet(url: String): String {
        val url = preferredUrl(url)
        ensureFreshClient()
        try {
            val response = httpClient.get(url) {
                headers {
                    append("Connection", "close")
                }
            }
            if (response.status == HttpStatusCode.OK) {
                lastClientUse = nowMs()
                return response.bodyAsText()
            } else {
                println("pureGet -> bad status, replacing HttpClient and retrying once")
                setHTTPClient()
                val retry = httpClient.get(url) {
                    headers {
                        append("Connection", "close")
                    }
                }
                return if (retry.status == HttpStatusCode.OK) {
                    lastClientUse = nowMs()
                    retry.bodyAsText()
                } else {
                    ""
                }
            }
        } catch (e: Throwable) {
            e.rethrowIfFatal()
            e.message?.let {
                if (it.contains("Unexpected status line: \r0")) {
                    return it.split("Unexpected status line: \r0")[1]
                }
            }
            failoverUrl(url, e, idempotent = true)?.let { return pureGet(it) }
            e.rethrowIfError()
            return ""
        }
    }

    val webSocketClient = HttpClient {
        install(WebSockets)
    }

    suspend fun webSocket(url: String, port: Int): String {
        var weight: String = ""
        try {
            webSocketClient.ws(
                method = HttpMethod.Get,
                host = url,
                port = port
            ) { // this: DefaultClientWebSocketSession
                val hey = 2
                // Send text frame.
                send("W")

                // Receive frame.
                val frame = incoming.receive()
                when (frame) {
                    is Frame.Text -> weight = (frame.readText())
                    else -> {}
                }
                return@ws
            }
        } catch (e: Exception) {
            println(e.message)
            return ""
        }
        return ""
    }
}